import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import { ArrowBack, Save, Delete } from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { examinationAPI, courseAPI } from '../../../services/api';

const ExaminationCreate = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [availableSemesters, setAvailableSemesters] = useState([]);
  const [availableSubjects, setAvailableSubjects] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    course: '',
    semester: '',
    startDate: null,
    endDate: null,
    registrationDeadline: null,
    questionsPerStudent: '',
    instructions: '',
  });

  const [selectedSubjects, setSelectedSubjects] = useState([]);

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    if (isEditMode && id && courses.length > 0) {
      fetchExamination();
    }
  }, [id, courses.length]);

  const fetchExamination = async () => {
    try {
      setLoading(true);
      const response = await examinationAPI.getOne(id);
      const exam = response.data.data?.examination || response.data.examination || response.data;

      setFormData({
        name: exam.name || '',
        code: exam.code || '',
        course: exam.course?._id || '',
        semester: exam.semester?.semesterId || '',
        startDate: exam.startDate ? new Date(exam.startDate) : null,
        endDate: exam.endDate ? new Date(exam.endDate) : null,
        registrationDeadline: exam.registrationDeadline ? new Date(exam.registrationDeadline) : null,
        questionsPerStudent: exam.questionsPerStudent || '',
        instructions: exam.instructions || '',
      });

      // Load course semesters
      if (exam.course?._id) {
        const course = courses.find(c => c._id === exam.course._id);
        if (course && course.semesters) {
          setAvailableSemesters(course.semesters);

          // Load semester subjects
          const semester = course.semesters.find(s => s._id === exam.semester?.semesterId);
          if (semester && semester.subjects) {
            setAvailableSubjects(semester.subjects);

            // Set selected subjects
            if (exam.subjects && exam.subjects.length > 0) {
              setSelectedSubjects(exam.subjects.map(s => ({
                subjectId: s.subjectId,
                name: s.name,
                code: s.code,
                totalMarks: s.totalMarks || 100,
                passingMarks: s.passingMarks || 40,
                duration: s.duration || 60,
                questionsLimit: s.questionsLimit ?? '',
              })));
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching examination:', error);
      toast.error('Failed to load examination');
      navigate('/admin/examinations');
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await courseAPI.getAll({ limit: 1000 });
      const coursesData = response.data.data || response.data || [];
      setCourses(coursesData);
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Failed to load courses');
    }
  };

  const handleCourseChange = (courseId) => {
    setFormData({
      ...formData,
      course: courseId,
      semester: '',
    });
    setAvailableSemesters([]);
    setAvailableSubjects([]);
    setSelectedSubjects([]);

    if (courseId) {
      const course = courses.find((c) => c._id === courseId);
      if (course && course.semesters) {
        setAvailableSemesters(course.semesters);
      }
    }
  };

  const handleSemesterChange = (semesterId) => {
    setFormData({
      ...formData,
      semester: semesterId,
    });
    setAvailableSubjects([]);
    setSelectedSubjects([]);

    if (semesterId) {
      const semester = availableSemesters.find((s) => s._id === semesterId);
      if (semester && semester.subjects) {
        setAvailableSubjects(semester.subjects);
      }
    }
  };

  const handleAddSubject = (subject) => {
    if (!selectedSubjects.find((s) => s.subjectId === subject._id)) {
      setSelectedSubjects([
        ...selectedSubjects,
          {
            subjectId: subject._id,
            name: subject.name,
            code: subject.code,
            totalMarks: 100,
            passingMarks: 40,
            duration: 60,
            questionsLimit: '',
          },
        ]);
      }
  };

  const handleRemoveSubject = (subjectId) => {
    setSelectedSubjects(selectedSubjects.filter((s) => s.subjectId !== subjectId));
  };

  const handleSubjectMarksChange = (subjectId, field, value) => {
    setSelectedSubjects(
      selectedSubjects.map((s) =>
        s.subjectId === subjectId
          ? { ...s, [field]: value === '' ? '' : Number(value) }
          : s
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.course || !formData.semester || selectedSubjects.length === 0) {
      toast.error('Please select Course, Semester, and at least one Subject');
      return;
    }

    if (!formData.startDate || !formData.endDate) {
      toast.error('Please select start and end dates');
      return;
    }

    if (formData.endDate <= formData.startDate) {
      toast.error('End date must be after start date');
      return;
    }

    // Validate subject marks
    const invalidSubjects = selectedSubjects.filter(
      (s) => (
        s.passingMarks > s.totalMarks ||
        s.totalMarks <= 0 ||
        s.passingMarks < 0 ||
        s.duration <= 0 ||
        (s.questionsLimit !== '' && Number(s.questionsLimit) < 1)
      )
    );
    if (invalidSubjects.length > 0) {
      toast.error('Invalid subject configuration. Check marks, duration, and question limits.');
      return;
    }

    try {
      setLoading(true);

      const semester = availableSemesters.find((s) => s._id === formData.semester);

      const payload = {
        name: formData.name,
        code: formData.code.toUpperCase(),
        course: formData.course,
        semester: {
          semesterId: semester._id,
          semesterNumber: semester.semesterNumber,
          semesterName: semester.name,
        },
        subjects: selectedSubjects.map((subject) => ({
          subjectId: subject.subjectId,
          name: subject.name,
          code: subject.code,
          totalMarks: Number(subject.totalMarks),
          passingMarks: Number(subject.passingMarks),
          duration: Number(subject.duration),
          ...(subject.questionsLimit !== '' && Number(subject.questionsLimit) > 0
            ? { questionsLimit: Number(subject.questionsLimit) }
            : {})
        })),
        startDate: formData.startDate,
        endDate: formData.endDate,
        registrationDeadline: formData.registrationDeadline,
        questionsPerStudent: parseInt(formData.questionsPerStudent),
        instructions: formData.instructions,
      };

      if (isEditMode) {
        await examinationAPI.update(id, payload);
        toast.success('Examination updated successfully');
      } else {
        await examinationAPI.create(payload);
        toast.success('Examination created successfully');
      }
      navigate('/admin/examinations');
    } catch (error) {
      console.error('Error creating examination:', error);
      toast.error(error.response?.data?.message || 'Failed to create examination');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const getTotalMarks = () => {
    return selectedSubjects.reduce((sum, subject) => sum + subject.totalMarks, 0);
  };

  const getTotalPassingMarks = () => {
    return selectedSubjects.reduce((sum, subject) => sum + subject.passingMarks, 0);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {isEditMode ? 'Edit Examination' : 'Create Examination'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {isEditMode ? 'Update examination details' : 'Set up a new examination with course, subjects, and schedule'}
          </Typography>
        </Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/examinations')}>
          Back to Examinations
        </Button>
      </Box>

      <form onSubmit={handleSubmit}>
        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Basic Information
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Examination Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                fullWidth
                placeholder="e.g., Annual Exam 2025"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Examination Code"
                name="code"
                value={formData.code}
                onChange={handleChange}
                required
                fullWidth
                inputProps={{ style: { textTransform: 'uppercase' } }}
                placeholder="e.g., AE-2025"
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Course & Semester
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Course</InputLabel>
                <Select
                  value={formData.course}
                  onChange={(e) => handleCourseChange(e.target.value)}
                  label="Course"
                  MenuProps={{ PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } } }}
                >
                  {courses.map((course) => (
                    <MenuItem key={course._id} value={course._id}>
                      {course.name} ({course.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required disabled={!formData.course}>
                <InputLabel>Semester</InputLabel>
                <Select
                  value={formData.semester}
                  onChange={(e) => handleSemesterChange(e.target.value)}
                  label="Semester"
                >
                  {availableSemesters.map((sem) => (
                    <MenuItem key={sem._id} value={sem._id}>
                      {sem.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {availableSemesters.length === 0 && formData.course && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  No semesters found for this course. Please add semesters in Course Management first.
                </Alert>
              </Grid>
            )}
          </Grid>
        </Paper>

        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Subjects & Marks Configuration
          </Typography>

          {!formData.semester ? (
            <Alert severity="info">Please select a course and semester first</Alert>
          ) : availableSubjects.length === 0 ? (
            <Alert severity="warning">
              No subjects found for this semester. Please add subjects in Course Management first.
            </Alert>
          ) : (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Available Subjects (Click to add):
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {availableSubjects.map((subject) => (
                    <Button
                      key={subject._id}
                      variant={
                        selectedSubjects.find((s) => s.subjectId === subject._id)
                          ? 'contained'
                          : 'outlined'
                      }
                      size="small"
                      onClick={() => handleAddSubject(subject)}
                      disabled={!!selectedSubjects.find((s) => s.subjectId === subject._id)}
                    >
                      {subject.code} - {subject.name}
                    </Button>
                  ))}
                </Box>
              </Box>

              {selectedSubjects.length > 0 && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    Selected Subjects ({selectedSubjects.length}):
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Subject Code</TableCell>
                          <TableCell>Subject Name</TableCell>
                          <TableCell align="center" width={120}>Total Marks</TableCell>
                          <TableCell align="center" width={120}>Passing Marks</TableCell>
                          <TableCell align="center" width={120}>Duration (min)</TableCell>
                          <TableCell align="center" width={130}>Questions Limit</TableCell>
                          <TableCell align="center" width={80}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedSubjects.map((subject) => (
                          <TableRow key={subject.subjectId}>
                            <TableCell>{subject.code}</TableCell>
                            <TableCell>{subject.name}</TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={subject.totalMarks}
                                onChange={(e) =>
                                  handleSubjectMarksChange(subject.subjectId, 'totalMarks', e.target.value)
                                }
                                inputProps={{ min: 1 }}
                                sx={{ width: 100 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={subject.passingMarks}
                                onChange={(e) =>
                                  handleSubjectMarksChange(subject.subjectId, 'passingMarks', e.target.value)
                                }
                                inputProps={{ min: 0, max: subject.totalMarks }}
                                sx={{ width: 100 }}
                                error={subject.passingMarks > subject.totalMarks}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={subject.duration}
                                onChange={(e) =>
                                  handleSubjectMarksChange(subject.subjectId, 'duration', e.target.value)
                                }
                                inputProps={{ min: 1 }}
                                sx={{ width: 100 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={subject.questionsLimit}
                                onChange={(e) =>
                                  handleSubjectMarksChange(subject.subjectId, 'questionsLimit', e.target.value)
                                }
                                inputProps={{ min: 1 }}
                                sx={{ width: 110 }}
                                placeholder="Auto"
                                helperText="Optional"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveSubject(subject.subjectId)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell colSpan={2} sx={{ fontWeight: 600 }}>
                            <strong>Total</strong>
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>
                            {getTotalMarks()}
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>
                            {getTotalPassingMarks()}
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>
                            {selectedSubjects.reduce((sum, s) => sum + (Number(s.duration) || 0), 0)} min
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>
                            {selectedSubjects.reduce((sum, s) => sum + (Number(s.questionsLimit) || 0), 0) || '-'}
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </>
          )}
        </Paper>

        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Schedule
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <DateTimePicker
                label="Start Date & Time"
                value={formData.startDate}
                onChange={(newValue) => {
                  // If no time is set, default to 00:00 (12 AM)
                  if (newValue && !formData.startDate) {
                    newValue.setHours(0, 0, 0, 0);
                  }
                  setFormData({ ...formData, startDate: newValue });
                }}
                slotProps={{
                  textField: {
                    required: true,
                    fullWidth: true,
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <DateTimePicker
                label="End Date & Time"
                value={formData.endDate}
                onChange={(newValue) => {
                  // If no time is set, default to 00:00 (12 AM)
                  if (newValue && !formData.endDate) {
                    newValue.setHours(0, 0, 0, 0);
                  }
                  setFormData({ ...formData, endDate: newValue });
                }}
                slotProps={{
                  textField: {
                    required: true,
                    fullWidth: true,
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <DateTimePicker
                label="Registration Deadline"
                value={formData.registrationDeadline}
                onChange={(newValue) => {
                  if (newValue && !formData.registrationDeadline) {
                    newValue.setHours(23, 59, 59, 999);
                  }
                  setFormData({ ...formData, registrationDeadline: newValue });
                }}
                slotProps={{
                  textField: {
                    required: true,
                    fullWidth: true,
                    helperText: "Last date for branch admins to assign students",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Questions Per Student"
                name="questionsPerStudent"
                type="number"
                value={formData.questionsPerStudent}
                onChange={handleChange}
                required
                fullWidth
                inputProps={{ min: 1 }}
                helperText="Legacy global total used as fallback when a subject does not have its own questions limit"
              />
            </Grid>

            <Grid item xs={12}>
              <Alert severity="info">
                Students can start the exam between start and end dates. The registration deadline determines when branch admins can assign students. Subject-specific question limits take precedence; otherwise the legacy global total is distributed across subjects.
              </Alert>
            </Grid>
          </Grid>
        </Paper>

        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Instructions
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Instructions for Students"
                name="instructions"
                value={formData.instructions}
                onChange={handleChange}
                fullWidth
                multiline
                rows={4}
                placeholder="Enter examination instructions, rules, and guidelines for students..."
              />
            </Grid>
          </Grid>
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/admin/examinations')} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" startIcon={<Save />} disabled={loading}>
            {loading ? 'Creating...' : 'Create Examination'}
          </Button>
        </Box>
      </form>
    </Box>
    </LocalizationProvider>
  );
};

export default ExaminationCreate;
